import React from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { FormattedMessage } from '../../util/reactIntl';

import { pickFooterSeoLinks, pickRelatedSeoLinks } from '../../util/seoLandingPages';

import { NamedLink } from '../../components';

import css from './SeoLinks.module.css';

/**
 * Crawlable internal links to the currently eligible SEO landing pages
 * (`/sewa-...`). Rendered as plain anchors (NamedLink -> <a href>), in the
 * server-rendered HTML, so search engines can discover the pages without
 * relying on the sitemap alone.
 *
 * - On an eligible SEO page: related pages (same category in other
 *   locations, then other categories in the same location).
 * - Everywhere else: the popular category-only pages.
 *
 * Renders inside the footer (SectionFooter). Renders nothing when there is nothing
 * eligible to link to.
 *
 * @component
 * @param {Object} props
 * @param {boolean} [props.standalone] add own padding/width when used outside the footer
 * @returns {JSX.Element|null}
 */
const SeoLinks = ({ standalone = false }) => {
  const eligiblePaths = useSelector(state => state.seoPages?.eligiblePaths);
  const seoCategories = useSelector(state => state.seoPages?.seoCategories);
  const { pathname } = useLocation();

  if (!eligiblePaths || eligiblePaths.length === 0) {
    return null;
  }

  const currentPath = pathname.replace(/\/$/, '');
  const isSeoPage = eligiblePaths.includes(currentPath);
  const links = isSeoPage
    ? pickRelatedSeoLinks(eligiblePaths, currentPath, seoCategories || undefined)
    : pickFooterSeoLinks(eligiblePaths, seoCategories || undefined);

  if (links.length === 0) {
    return null;
  }

  return (
    <nav
      className={classNames(css.root, { [css.standalone]: standalone })}
      aria-labelledby="seo-links-title"
    >
      <h2 id="seo-links-title" className={css.title}>
        <FormattedMessage id={isSeoPage ? 'SeoLinks.relatedTitle' : 'SeoLinks.popularTitle'} />
      </h2>
      <ul className={css.list}>
        {links.map(link => (
          <li key={link.path} className={css.item}>
            <NamedLink
              className={css.link}
              name="SeoLandingPage"
              params={{ seoSlug: link.seoSlug }}
            >
              {link.label}
            </NamedLink>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default SeoLinks;
